import { Link } from "@tanstack/react-router";
import { resolveImage } from "@/lib/product-images";
import { formatPrice } from "@/lib/format";

function formatReviews(n: number): string {
  if (!n || n <= 0) return "";
  if (n >= 1000) {
    const k = n / 1000;
    return `${k % 1 === 0 ? k.toFixed(0) : k.toFixed(1)}k`;
  }
  return String(n);
}

export function ProductCard({ product, priority = false }: { product: { id: string; slug: string; name: string; price_cents: number; compare_at_price_cents?: number | null; image_url: string; category: string; rating?: number | null; reviews_count?: number | null }; priority?: boolean }) {
  const mrp = product.compare_at_price_cents ?? null;
  const hasDiscount = mrp != null && mrp > product.price_cents;
  const off = hasDiscount ? Math.round(((mrp! - product.price_cents) / mrp!) * 100) : 0;
  const rating = product.rating ?? 0;
  const reviews = product.reviews_count ?? 0;
  const showRating = rating > 0;
  return (
    <Link to="/product/$slug" params={{ slug: product.slug }} className="group block bg-card rounded-md overflow-hidden border border-border/60 hover:shadow-lg transition-shadow">
      <div className="relative aspect-square overflow-hidden bg-muted">
        <img
          src={resolveImage(product.slug, product.image_url, 320)}
          srcSet={`${resolveImage(product.slug, product.image_url, 240)} 240w, ${resolveImage(product.slug, product.image_url, 360)} 360w, ${resolveImage(product.slug, product.image_url, 480)} 480w`}
          sizes="(max-width: 640px) 45vw, (max-width: 1024px) 30vw, 240px"
          alt={product.name}
          width={320}
          height={320}
          loading={priority ? "eager" : "lazy"}
          fetchPriority={priority ? "high" : "auto"}
          decoding="async"
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
        />


        {hasDiscount && (
          <span
            className={`absolute top-2 left-2 text-white text-xs font-bold px-2 py-0.5 rounded-sm shadow ${
              off >= 50 ? "bg-red-700" : off >= 25 ? "bg-orange-700" : "bg-emerald-800"
            }`}
          >
            {off}% OFF
          </span>
        )}
      </div>
      <div className="p-2.5">
        <p className="text-[13px] text-foreground line-clamp-1">{product.name}</p>
        <div className="flex items-baseline gap-1.5 mt-1">
          <p className="text-[15px] font-bold">{formatPrice(product.price_cents)}</p>
          {hasDiscount && (
            <p className="text-[11px] text-muted-foreground line-through">{formatPrice(mrp!)}</p>
          )}
        </div>
        {hasDiscount && (
          <p
            className={`text-xs font-semibold mt-0.5 ${
              off >= 50 ? "text-red-800" : off >= 25 ? "text-orange-800" : "text-emerald-800"
            }`}
          >
            {off}% off
          </p>
        )}
        {showRating && (
          <div className="flex items-center gap-1.5 mt-1.5">
            <span className="inline-flex items-center gap-0.5 bg-emerald-800 text-white text-xs font-semibold px-1.5 py-0.5 rounded">
              {rating.toFixed(1)} ★
            </span>
            {reviews > 0 && (
              <span className="text-xs text-muted-foreground">{formatReviews(reviews)} Reviews</span>
            )}
          </div>
        )}

        <p className="text-[11px] text-muted-foreground mt-1">Free Delivery</p>
      </div>
    </Link>
  );
}
